import type { HistoryCommand } from "../history/historyTypes";
import { hitTestBubble } from "./BubbleGeometry";
import { panelAtPoint } from "./PanelGeometry";
import { cloneDocumentState } from "./comicValidation";
import type { ComicBubbleObject, ComicPanel, PersistedDocumentState, Point } from "./internalTypes";
import type { ComicSelectionState } from "./comicTypes";
import type { PersistedLayerState } from "../layers/layerTypes";

function cloneObject(object: Readonly<ComicBubbleObject>): ComicBubbleObject {
  return { ...object, data: { ...object.data } };
}

export class ComicDocumentController {
  private readonly templateId: PersistedDocumentState["comicTemplateId"];
  private readonly panels: ComicPanel[];
  private objects: ComicBubbleObject[];
  private layers: PersistedLayerState[];
  private activeLayerId: string;
  private selectedPanelId: string | null;
  private selectedObjectId: string | null = null;

  public constructor(state: PersistedDocumentState) {
    const cloned = cloneDocumentState(state);
    this.templateId = cloned.comicTemplateId;
    this.panels = cloned.panels;
    this.objects = cloned.objects;
    this.layers = cloned.layers;
    this.activeLayerId = cloned.activeLayerId;
    this.selectedPanelId = this.panels[0]?.id ?? null;
  }

  public getPanels(): ReadonlyArray<ComicPanel> {
    return this.panels;
  }

  public getObjects(): ReadonlyArray<ComicBubbleObject> {
    return this.objects;
  }

  public setObjects(objects: ReadonlyArray<ComicBubbleObject>): void {
    this.objects = objects.map(cloneObject);
    if (
      this.selectedObjectId &&
      !this.objects.some((object) => object.id === this.selectedObjectId)
    ) {
      this.selectedObjectId = null;
    }
  }

  public getSnapshot(): PersistedDocumentState {
    return {
      schemaVersion: 2,
      activeLayerId: this.activeLayerId,
      layers: this.layers.map((layer) => ({ ...layer })),
      comicTemplateId: this.templateId,
      panels: this.panels.map((panel) => ({ ...panel })),
      objects: this.objects.map(cloneObject),
    };
  }

  public getLayers(): ReadonlyArray<PersistedLayerState> {
    return this.layers;
  }

  public getActiveLayerId(): string {
    return this.activeLayerId;
  }

  public setLayerState(layers: ReadonlyArray<PersistedLayerState>, activeLayerId: string): void {
    this.layers = layers.map((layer) => ({ ...layer }));
    this.activeLayerId = activeLayerId;
  }

  public getSelection(preview?: ComicBubbleObject | null): ComicSelectionState {
    const selected = preview ?? this.objects.find((object) => object.id === this.selectedObjectId);
    return {
      panelId: this.selectedPanelId,
      object: selected ? cloneObject(selected) : null,
    };
  }

  public selectPanel(panelId: string): boolean {
    if (!this.panels.some((panel) => panel.id === panelId)) return false;
    const changed = this.selectedPanelId !== panelId;
    this.selectedPanelId = panelId;
    if (this.selectedObjectId) {
      const selected = this.objects.find((object) => object.id === this.selectedObjectId);
      if (selected?.panelId !== panelId) this.selectedObjectId = null;
    }
    return changed;
  }

  public selectPanelAt(point: Point): ComicPanel | undefined {
    const panel = panelAtPoint(this.panels, point);
    if (panel) this.selectPanel(panel.id);
    return panel;
  }

  public getSelectedPanel(): ComicPanel | undefined {
    return this.panels.find((panel) => panel.id === this.selectedPanelId);
  }

  public getSelectedObject(): ComicBubbleObject | undefined {
    return this.objects.find((object) => object.id === this.selectedObjectId);
  }

  public selectObjectAt(
    point: Point,
    handleRadius: number,
  ): { object: ComicBubbleObject; region: "body" | "resize" | "rotate" } | undefined {
    const selected = this.getSelectedObject();
    if (selected) {
      const region = hitTestBubble(selected, point, handleRadius);
      if (region) return { object: cloneObject(selected), region };
    }
    const object = [...this.objects]
      .sort((left, right) => right.order - left.order)
      .find((candidate) => hitTestBubble(candidate, point, 0) === "body");
    if (!object) {
      this.selectedObjectId = null;
      return undefined;
    }
    this.selectedPanelId = object.panelId;
    this.selectedObjectId = object.id;
    return { object: cloneObject(object), region: "body" };
  }

  public selectObject(objectId: string | null): boolean {
    if (objectId === null) {
      const changed = this.selectedObjectId !== null;
      this.selectedObjectId = null;
      return changed;
    }
    const object = this.objects.find((candidate) => candidate.id === objectId);
    if (!object) return false;
    const changed = this.selectedObjectId !== objectId;
    this.selectedObjectId = objectId;
    this.selectedPanelId = object.panelId;
    return changed;
  }

  public applyObjectCommand(command: HistoryCommand): void {
    switch (command.kind) {
      case "addComicObject":
        this.objects = [
          ...this.objects.filter((item) => item.id !== command.object.id),
          cloneObject(command.object),
        ];
        break;
      case "transformComicObject":
        this.objects = this.objects.map((item) =>
          item.id === command.after.id ? cloneObject(command.after) : item,
        );
        break;
      case "editComicText":
        this.objects = this.objects.map((item) =>
          item.id === command.objectId
            ? { ...item, data: { ...item.data, text: command.afterText } }
            : item,
        );
        break;
      case "deleteComicObject":
        this.objects = this.objects.filter((item) => item.id !== command.object.id);
        break;
      case "clearPanel":
        this.objects = this.objects.filter((item) => item.panelId !== command.panelId);
        break;
      case "duplicatePanel":
        this.objects = [
          ...this.objects.filter((item) => item.panelId !== command.targetPanelId),
          ...command.duplicatedObjects.map(cloneObject),
        ];
        break;
      case "stroke":
      case "layerMutation":
      case "clearLayer":
      case "mergeLayerDown":
        break;
    }
    if (
      this.selectedObjectId &&
      !this.objects.some((object) => object.id === this.selectedObjectId)
    ) {
      this.selectedObjectId = null;
    }
  }
}
